import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/api/client'
import { Icon } from '@/components/Icon'
import { AttemptFailure } from '@/components/JobFailure'
import { TelemetryLoading } from '@/components/JobPanel'
import { NODE_STATES, PREPARING_JOB, StepMeter } from '@/components/RunFleet'
import { SidePanel } from '@/components/SidePanel'
import { nodePrompt } from '@/lib/failurePrompt'
import type { NodeDetail, Rollout, RunConfiguration, RunNode, TaskInfo } from '@/lib/runs'

/**
 * One worker node's peek, the counterpart of the job panel.
 *
 * A node failure is not a property of the job that happened to be on it: the node
 * takes the next job and breaks that one too. The lane cards make every failure
 * look like a task's, so the node needed the same surface — what state it is in,
 * what it is answerable for, and its own log.
 */
export function NodePanel({ runId, runName, configuration, node, jobs, tasks, onSelectJob, onClose }: {
  runId: string; runName: string; configuration: RunConfiguration
  node: RunNode; jobs: Rollout[]; tasks: Map<string, TaskInfo>
  onSelectJob: (jobId: string) => void; onClose: () => void
}) {
  const [detail, setDetail] = useState<NodeDetail>()
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const state = NODE_STATES[node.state] ?? NODE_STATES.pending

  const load = useCallback(async () => {
    if (document.hidden) return
    try { setDetail(await api.get<NodeDetail>(`/eng/runs/${runId}/nodes/${node.instance_id}`)) }
    catch (e) { setError(e instanceof ApiError ? e.message : (e as Error).message) }
    finally { setLoaded(true) }
  }, [runId, node.instance_id])
  useEffect(() => { setDetail(undefined); setLoaded(false); setError('') }, [node.instance_id])
  // A live node keeps writing; a stopped one has said everything it is going to.
  useEffect(() => {
    load()
    if (!node.live) return
    const timer = window.setInterval(load, 10_000)
    return () => clearInterval(timer)
  }, [load, node.live])

  /** The panel shows the tail; the export is the whole log, assembled server-side
   *  with the node's state and the attempts it is answerable for. */
  const [exporting, setExporting] = useState(false)
  const exportLog = useCallback(async () => {
    setExporting(true)
    try {
      const value = await api.get<{ filename: string; markdown: string }>(
        `/eng/runs/${runId}/nodes/${node.instance_id}/report`)
      const url = URL.createObjectURL(new Blob([value.markdown], { type: 'text/markdown' }))
      const link = document.createElement('a')
      link.href = url
      link.download = value.filename
      link.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message)
    } finally {
      setExporting(false)
    }
  }, [runId, node.instance_id])

  const copyPrompt = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(nodePrompt({ runId, runName, configuration, node, detail }))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not write to the clipboard.')
    }
  }, [runId, runName, configuration, node, detail])

  const byJobId = new Map(jobs.map(job => [job.job_id, job]))
  const failures = detail?.failures ?? []
  return <SidePanel label={`node ${node.instance_id}`} onClose={onClose} head={
    <div>
      <span className="eyebrow">Worker node</span>
      <h2>{node.instance_id}</h2>
      <div className="row job-panel-chips">
        <span className={`chip ${state.tone}`}>{state.label}</span>
        {node.infra_total > 0 && <span className="chip err">{node.infra_total} infra</span>}
        {node.infra_streak > 0 && <span className="chip err" title="Ten consecutive node-attributable failures quarantine the node">streak {node.infra_streak}/10</span>}
      </div>
    </div>
  }>
    {error && <div className="note err small">{error}</div>}

    <p className="node-panel-activity">{node.activity}</p>

    <section className="panel-section">
      <div className="panel-stats">
        <div><span>Slots busy</span><strong>{node.busy}/{node.slots}</strong></div>
        <div><span>Jobs completed</span><strong>{node.completed}</strong></div>
        <div><span>Infra failures</span><strong className={node.infra_total ? 'err-text' : ''}>{node.infra_total}</strong></div>
        <div><span>Rate-limited</span><strong>{node.rate_limited}</strong></div>
      </div>
      <dl className="panel-facts">
        <dt>State</dt><dd>{state.label}{node.phase && <span className="muted small"> · reported phase <code>{node.phase}</code></span>}</dd>
        <dt>Heartbeat</dt><dd>{node.age_seconds == null ? 'None published' : `${Math.round(node.age_seconds)}s ago`}{node.live ? '' : ' · not live'}</dd>
        {node.last_seen && <><dt>Last seen</dt><dd>{node.last_seen}</dd></>}
      </dl>
    </section>

    {node.running.length > 0 && <section className="panel-section">
      <h3>Running now</h3>
      <ul className="node-jobs">{node.running.map(job => {
        const row = job.job_id ? byJobId.get(job.job_id) : undefined
        return <li key={job.attempt_id ?? `${job.task}-${job.seed}`}>
          <button type="button" className="node-job" onClick={() => job.job_id && onSelectJob(job.job_id)} disabled={!job.job_id}>
            <span className="node-job-task">{tasks.get(job.task)?.title ?? job.task}</span>
            {row?.phase === 'preparing'
              ? <span className="job-phase" title={PREPARING_JOB}>preparing</span>
              : <StepMeter step={job.latest_sequence} maxSteps={row?.max_steps} />}
          </button>
        </li>
      })}</ul>
    </section>}

    <section className="panel-section">
      <div className="panel-section-head">
        <h3>Infrastructure failures</h3>
        <div className="failure-actions">
          <button type="button" className="report-export" onClick={exportLog} disabled={exporting}>
            <Icon name="file" size={15} />{exporting ? 'Exporting…' : 'Export log'}
          </button>
          <button type="button" className="report-export" onClick={copyPrompt}
                  title="Copy a brief an agent can act on: the two repositories, this node, and its log">
            <Icon name="goofy" size={16} />{copied ? 'Copied' : 'Copy prompt'}
          </button>
        </div>
      </div>
      {!loaded ? <TelemetryLoading label="Reading this node’s record…" />
        : failures.length ? failures.map(row => <div className="node-failure" key={`${row.job_id}-${row.ordinal}`}>
            <button type="button" className="link-button" onClick={() => onSelectJob(row.job_id)}>
              {tasks.get(row.task_id ?? '')?.title ?? row.task_id ?? row.job_id}
              <span className="muted"> · {row.job_id}</span>
            </button>
            <AttemptFailure attempt={row.attempt} ordinal={row.ordinal} output={{ runId, jobId: row.job_id }} />
          </div>)
          : <p className="empty small">{detail?.attributed
              ? 'No failed attempt on this node’s log. Everything it claimed, it finished.'
              : 'This node has published no log yet, so nothing can be attributed to it.'}</p>}
    </section>

    <section className="panel-section">
      <h3>Node log</h3>
      {!loaded ? <TelemetryLoading label="Fetching the node log…" />
        : detail?.logs.length ? detail.logs.map(log => <div className="step-block" key={log.title}>
            <h4>{log.title}</h4>
            <pre className="node-log">{log.text}</pre>
          </div>)
          : <p className="empty small">This node has written no log to the run bucket.</p>}
    </section>
  </SidePanel>
}
